import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import businessCategoryService from '../../API/businessCategoryApi';
import { RootState } from '../store';
import {
	BusinessCategoryState,
	BusinessCategoryType,
	BusinessCategoryValues,
} from '../../components/business-category/types';

//*** Initial state
const initialState: BusinessCategoryState = {
	data: [],
	isLoading: false,
};

export const asyncRetrieveBusinessCategories = createAsyncThunk<
	BusinessCategoryType[],
	void,
	{ state: RootState; rejectValue: string }
>('businessCategory/retrieve', async (_, thunkApi) => {
	try {
		const res = await businessCategoryService.retrieveBusinesses();
		return res.data.data;
	} catch (error: any) {
		const errorMessage = (error.response?.data?.message ?? error.message) || error.toString();
		toast.error(errorMessage);
		return thunkApi.rejectWithValue(errorMessage);
	}
});

export const asyncCreateBusinessCategory = createAsyncThunk<
	BusinessCategoryType,
	BusinessCategoryValues,
	{ state: RootState; rejectValue: string }
>('businessCategory/create', async (payload, thunkApi) => {
	try {
		const res = await businessCategoryService.createBusinessCategory(payload);
		toast.success(res.data.message);
		return res.data.data;
	} catch (error: any) {
		const errorMessage = (error.response?.data?.message ?? error.message) || error.toString();
		toast.error(errorMessage);
		return thunkApi.rejectWithValue(errorMessage);
	}
});

export const asyncUpdateBusinessCategory = createAsyncThunk<
	{ id: string; updatedCategory: Partial<BusinessCategoryValues> },
	{ id: string; updatedCategory: Partial<BusinessCategoryValues> },
	{ state: RootState; rejectValue: string }
>('businessCategory/update', async ({ id, updatedCategory }, thunkApi) => {
	try {
		const res = await businessCategoryService.UpdateBusinessCategory(id, updatedCategory);
		toast.success(res.data.message);
		return { id, updatedCategory };
	} catch (error: any) {
		const errorMessage = (error.response?.data?.message ?? error.message) || error.toString();
		toast.error(errorMessage);
		return thunkApi.rejectWithValue(errorMessage);
	}
});

export const asyncDeleteBusinessCategory = createAsyncThunk<
	string,
	string,
	{ state: RootState; rejectValue: string }
>('businessCategory/delete', async (id, thunkApi) => {
	try {
		const res = await businessCategoryService.deleteBusinessCategory(id);
		toast.success(res.data.message);
		return id;
	} catch (error: any) {
		const errorMessage = (error.response?.data?.message ?? error.message) || error.toString();
		toast.error(errorMessage);
		return thunkApi.rejectWithValue(errorMessage);
	}
});

const BusinessCategorySlice = createSlice({
	name: 'businessCategory',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			//**** Retrieve categories
			.addCase(asyncRetrieveBusinessCategories.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(
				asyncRetrieveBusinessCategories.fulfilled,
				(state, action: PayloadAction<BusinessCategoryType[]>) => {
					state.isLoading = false;
					state.data = action.payload;
				},
			)
			.addCase(asyncRetrieveBusinessCategories.rejected, (state) => {
				state.isLoading = false;
			})

			//*****  Create category
			.addCase(asyncCreateBusinessCategory.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(
				asyncCreateBusinessCategory.fulfilled,
				(state, action: PayloadAction<BusinessCategoryType>) => {
					state.isLoading = false;
					state.data = [action.payload, ...state.data];
				},
			)
			.addCase(asyncCreateBusinessCategory.rejected, (state) => {
				state.isLoading = false;
			})

			//****  Update category
			.addCase(
				asyncUpdateBusinessCategory.fulfilled,
				(
					state,
					action: PayloadAction<{
						id: string;
						updatedCategory: Partial<BusinessCategoryType>;
					}>,
				) => {
					const { id, updatedCategory } = action.payload;
					state.data = state.data.map((category) =>
						category.id === id ? { ...category, ...updatedCategory } : category,
					);
				},
			)
			.addCase(asyncUpdateBusinessCategory.rejected, (state) => {
				state.isLoading = false;
			})

			//**** Delete category
			.addCase(
				asyncDeleteBusinessCategory.fulfilled,
				(state, action: PayloadAction<string>) => {
					state.data = state.data.filter((category) => category.id !== action.payload);
				},
			)
			.addCase(asyncDeleteBusinessCategory.rejected, (state) => {
				state.isLoading = false;
			});
	},
});

export const allBusinessCategories = (state: RootState) => state.businessCategory;

export default BusinessCategorySlice.reducer;
